import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Menu, Radio, Badge, Button, Alert, TextInput, Select, CloseButton, SelectProps } from '@mantine/core';
import { HiShare, HiMail, HiPlusCircle } from 'react-icons/hi';
import { IconContext } from 'react-icons';
import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { match, P } from 'ts-pattern';
import { CopyLink } from 'components/form/CopyLink';
import { InviteLink, isLinkActive, useInviteLinks } from './admin/inviteLinks';
import { Workspace } from './workspace';
import { usePortal } from './portal';
import { DateTime as D } from 'luxon';
import { HrText, LabelForm } from 'shared/ui/Labels';
import { fmtDate } from 'shared/format';
import { InviteLinkCard } from './admin/AdminInvite';
import { isNullOrEmpty } from 'shared/collection';
import { twMerge } from 'tailwind-merge';
import { SAvatarGroup } from './SAvatar';
import { entries, pipe, take, values } from 'remeda';
import { useCombobox, Combobox } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { SAvatar } from './SAvatar';
import { Role, roleMd, zRoles } from 'shared/forecast/roleTypes';
import { unique } from 'remeda';
import { okOrThrow } from 'shared/promise';
import { sb } from 'utils/db';
import { Auth } from 'api/auth';
import { MutationButton, MutationError } from 'components/form/MutationButton';
import { notifications } from '@mantine/notifications';
import { uri } from 'shared/uri';
import { env } from 'utils/env';
import { useWindowInfo } from 'components/hooks/useWindowInfo';
import { SelectionOptionBasic } from 'components/form/SelectOption';
const shareTypes = ['existing', 'invite'] as const;
type ShareType = (typeof shareTypes)[number];
type AccessTypeMd = {
  label: string;
  description: string;
  config: {
    anon: boolean;
    roles: Role[];
  };
};
const accessTypeMd = {
  participant: {
    label: 'Participant',
    description: 'A temporary and anonymous forecaster. Usefull for sharing a question with a group of once-off forecasters',
    config: {
      anon: true,
      roles: ['forecaster']
    }
  },
  forecaster: {
    label: 'Forecaster',
    description: 'Standard user with access to forecast in this workspace',
    config: {
      anon: false,
      roles: ['forecaster']
    }
  },
  viewer: {
    label: 'Viewer',
    description: 'Standard user with ability to view and comment',
    config: {
      anon: false,
      roles: ['viewer']
    }
  }
} satisfies Record<string, AccessTypeMd>;
type AccessType = keyof typeof accessTypeMd;
type EmailCompletionProfile = {
  name: string;
  email: string;
  workspaces: string[];
  workspace_ids: string[];
};
const useShareButton = () => {
  const $ = _c(60);
  const [menuOpened, setMenuOpened] = useState(false);
  const [shareType, setShareType] = useState("existing");
  const [accessType, setAccessType] = useState("participant");
  const {
    profile,
    workspace_id
  } = usePortal();
  let t0;
  if ($[0] !== profile) {
    t0 = profile?.hasRole("admin");
    $[0] = profile;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isAdmin = t0;
  const windowInfo = useWindowInfo();
  const title = windowInfo.title ?? "Swift";
  let t1;
  if ($[2] !== windowInfo.url) {
    t1 = windowInfo.url ?? uri(env.URL);
    $[2] = windowInfo.url;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const url = t1;
  let t2;
  if ($[4] !== windowInfo.url?.path) {
    t2 = windowInfo.url?.path?.join("/") ?? "";
    $[4] = windowInfo.url?.path;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const path = t2;
  let t3;
  if ($[6] !== isAdmin || $[7] !== workspace_id) {
    t3 = {
      id: workspace_id,
      enabled: isAdmin
    };
    $[6] = isAdmin;
    $[7] = workspace_id;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const uWorkspace = Workspace.useAdminWorkspace(t3);
  let t4;
  if ($[9] !== path || $[10] !== uWorkspace) {
    t4 = {
      uWorkspace,
      path
    };
    $[9] = path;
    $[10] = uWorkspace;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const inviteLinks = useInviteLinks(t4);
  const emailCombo = useCombobox();
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [selectedEmails, setSelectedEmails] = useState(t5);
  const [roleForNewUsers, setRoleForNewUsers] = useState("forecaster");
  const [emailInput, setEmailInput] = useState("");
  let t6;
  if ($[13] !== selectedEmails) {
    t6 = selectedEmails.some(_temp);
    $[13] = selectedEmails;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const hasNewUsers = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = Object.values(zRoles.enum).filter(_temp2);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const availableRoles = t7;
  const [emailDebounced] = useDebouncedValue(emailInput, 200);
  let t8;
  if ($[16] !== emailDebounced || $[17] !== workspace_id) {
    t8 = ["emailCompletions", "share", emailDebounced, workspace_id];
    $[16] = emailDebounced;
    $[17] = workspace_id;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== emailDebounced) {
    t9 = async () => {
      if (emailDebounced == null || emailDebounced.length <= 2) {
        return [];
      }
      const result = await sb.rpc("email_completions", {
        value: emailDebounced
      });
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    };
    $[19] = emailDebounced;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = isAdmin && !!workspace_id && emailDebounced.length > 2;
  let t11;
  if ($[21] !== t10 || $[22] !== t8 || $[23] !== t9) {
    t11 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10
    };
    $[21] = t10;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const emailCompletionsQuery = useQuery(t11);
  let t12;
  if ($[25] !== emailCombo || $[26] !== selectedEmails) {
    t12 = (email, profile_0, workspaceId) => {
      const isExisting = !!profile_0;
      const isInCurrentWorkspace = workspaceId ? profile_0?.workspace_ids?.includes(workspaceId) : false;
      if (!selectedEmails.find(e_0 => e_0.email.toLowerCase() == email.toLowerCase())) {
        setSelectedEmails(prev => [...prev, {
          email,
          isExisting,
          profile: profile_0,
          isInCurrentWorkspace
        }]);
      }
      setEmailInput("");
      emailCombo.closeDropdown();
    };
    $[25] = emailCombo;
    $[26] = selectedEmails;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = email_0 => {
      setSelectedEmails(prev_0 => prev_0.filter(e_1 => e_1.email != email_0));
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== path || $[30] !== roleForNewUsers || $[31] !== selectedEmails || $[32] !== title || $[33] !== workspace_id) {
    t14 = async () => {
      const result_1 = await Auth.shareLink({
        emails: selectedEmails.map(_temp4),
        role: roleForNewUsers,
        workspace_id,
        path,
        title
      });
      return okOrThrow(result_1);
    };
    $[29] = path;
    $[30] = roleForNewUsers;
    $[31] = selectedEmails;
    $[32] = title;
    $[33] = workspace_id;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== emailCombo) {
    t15 = result_2 => {
      notifications.show({
        title: "Sharing emails sent",
        message: `Shared with ${result_2.emailsSent} users${result_2.emailsSent === 1 ? "" : "s"}`,
        color: "green"
      });
      setSelectedEmails([]);
      setEmailInput("");
      emailCombo.closeDropdown();
      setMenuOpened(false);
    };
    $[35] = emailCombo;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = {
      mutationFn: t14,
      onSuccess: t15
    };
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = useMutation(t16);
  let t18;
  if ($[40] !== emailCombo || $[41] !== emailCompletionsQuery || $[42] !== emailInput || $[43] !== roleForNewUsers || $[44] !== selectedEmails || $[45] !== t12 || $[46] !== t17) {
    t18 = {
      selectedEmails,
      setSelectedEmails,
      addEmail: t12,
      removeEmail: t13,
      emailInput,
      setEmailInput,
      isValidEmail: _temp3,
      emailCompletionsQuery,
      emailCombo,
      roleForNewUsers,
      setRoleForNewUsers,
      availableRoles,
      sendEmailMute: t17
    };
    $[40] = emailCombo;
    $[41] = emailCompletionsQuery;
    $[42] = emailInput;
    $[43] = roleForNewUsers;
    $[44] = selectedEmails;
    $[45] = t12;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const email_2 = t18;
  let t19;
  if ($[48] !== accessType || $[49] !== email_2 || $[50] !== hasNewUsers || $[51] !== inviteLinks || $[52] !== isAdmin || $[53] !== menuOpened || $[54] !== path || $[55] !== shareType || $[56] !== title || $[57] !== url || $[58] !== workspace_id) {
    t19 = {
      inviteLinks,
      menuOpened,
      setMenuOpened,
      email: email_2,
      shareType,
      setShareType,
      accessType,
      setAccessType,
      isAdmin,
      hasNewUsers,
      workspace_id,
      url,
      title,
      path
    };
    $[48] = accessType;
    $[49] = email_2;
    $[50] = hasNewUsers;
    $[51] = inviteLinks;
    $[52] = isAdmin;
    $[53] = menuOpened;
    $[54] = path;
    $[55] = shareType;
    $[56] = title;
    $[57] = url;
    $[58] = workspace_id;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  return t19;
};
type UseShareButton = ReturnType<typeof useShareButton>;
const renderRoleOption: SelectProps['renderOption'] = ({
  option,
  checked
}) => <SelectionOptionBasic title={option.label} checked={checked} desc={roleMd[option.value as Role].desc} />;
export const ShareButton = () => {
  const $ = _c(42);
  const uShare = useShareButton();
  const {
    inviteLinks,
    url,
    shareType,
    setShareType,
    accessType,
    setAccessType,
    isAdmin,
    hasNewUsers,
    menuOpened,
    setMenuOpened,
    email,
    title,
    path,
    workspace_id
  } = uShare;
  let t0;
  if ($[0] !== path) {
    t0 = link => isLinkActive({
      l: link,
      showExpired: false,
      path
    });
    $[0] = path;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isLinkRelevant = t0;
  const isLinkMatchingConfig = _temp5;
  let t1;
  if ($[2] !== inviteLinks.links || $[3] !== isLinkRelevant) {
    t1 = accessTypeValue => {
      const config_0 = accessTypeMd[accessTypeValue].config;
      if (!config_0) {
        return;
      }
      const matchingLinks = inviteLinks.links?.filter(l => isLinkRelevant(l) && isLinkMatchingConfig(l, config_0));
      if (isNullOrEmpty(matchingLinks)) {
        return;
      }
      return matchingLinks.sort(_temp6)[0];
    };
    $[2] = inviteLinks.links;
    $[3] = isLinkRelevant;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getBestMatchingLink = t1;
  let t2;
  if ($[5] !== inviteLinks.links) {
    t2 = inviteLinks.links?.sort(_temp7)[0];
    $[5] = inviteLinks.links;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const mostRecentMatchingLink = t2;
  const activeLink = getBestMatchingLink(accessType);
  const {
    profilesUI,
    capture
  } = usePortal();
  let t3;
  if ($[7] !== capture || $[8] !== path || $[9] !== setMenuOpened || $[10] !== workspace_id) {
    t3 = opened => {
      setMenuOpened(o => {
        if (opened == o) {
          return opened;
        }
        if (opened) {
          capture("share_opened", {
            path,
            workspace_id
          });
        }
        return opened;
      });
    };
    $[7] = capture;
    $[8] = path;
    $[9] = setMenuOpened;
    $[10] = workspace_id;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const onMenuChange = t3;
  let t4;
  if ($[12] !== mostRecentMatchingLink || $[13] !== setAccessType || $[14] !== setShareType) {
    t4 = () => {
      if (mostRecentMatchingLink) {
        setShareType("invite");
        const matchingAccessType = pipe(entries(accessTypeMd).find(t5 => {
          const [, t] = t5;
          return isLinkMatchingConfig(mostRecentMatchingLink, t.config);
        }), _temp8);
        if (matchingAccessType) {
          setAccessType(matchingAccessType);
        }
      } else {
        setShareType("existing");
      }
    };
    $[12] = mostRecentMatchingLink;
    $[13] = setAccessType;
    $[14] = setShareType;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== url.path) {
    t5 = url.path?.join("/");
    $[16] = url.path;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t5) {
    t6 = [t5];
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useEffect(t4, t6);
  const T0 = Menu;
  const t7 = !email.emailCombo.dropdownOpened;
  let t8;
  if ($[20] !== onMenuChange) {
    t8 = () => onMenuChange(true);
    $[20] = onMenuChange;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <HiShare />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8) {
    t10 = <Menu.Target><ActionIcon onClick={t8}>{t9}</ActionIcon></Menu.Target>;
    $[23] = t8;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = Menu;
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      dropdown: "!p-4 overflow-y-auto max-h-[80vh]"
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = IconContext;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      size: undefined,
      color: undefined
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = isAdmin ? <div className="w-[min(50em,95vw)] flex flex-col gap-4"><div><h3 className="text-lg font-semibold mb-4"><span className="opacity-60">Share page:</span> <span className="font-bold">{title}</span></h3><Radio.Group value={shareType} onChange={t_1 => setShareType(t_1 as ShareType)}><div className="flex gap-2">{shareTypes.map(t_2 => <ShareRadioCard key={t_2} value={t_2} label={match(t_2).with("existing", _temp9).with("invite", _temp10).exhaustive()} description={t_2 == "existing" ? "Share the link to this page for existing users to use" : "Share the link to invite someone to join this workspace"} checked={t_2 == shareType} />)}</div></Radio.Group></div>{match(shareType).with("existing", () => {
      const users = profilesUI ? values(profilesUI).filter(_temp11) : [];
      const usersToShow = take(users, 20);
      const more = users.length > usersToShow.length ? users.length - usersToShow.length : undefined;
      return <div className="flex flex-col gap-2 mt-4"><LabelForm>Share this link with users in this workspace</LabelForm><div className="flex gap-2 items-center">{profilesUI && <div className="flex gap-1 items-center"><SAvatarGroup profiles={usersToShow} />{more != null && <div className="text-sm opacity-60">and {more} more</div>}</div>}<CopyLink url={url.url} hideUrl={true} buttonProps={{
            variant: "filled"
          }} className="ml-auto">Copy link</CopyLink></div><HrText>or</HrText><EmailShareForm {...email} /><div className="flex gap-4 self-end items-end">{hasNewUsers && <div><Select label="Access for new users" data={email.availableRoles.map(_temp12)} value={email.roleForNewUsers} onChange={value => {
              if (value == null) {
                return;
              }
              email.setRoleForNewUsers(zRoles.parse(value));
            }} size="md" className="w-48" comboboxProps={{
              withinPortal: false
            }} renderOption={renderRoleOption} /></div>}<MutationButton variant="filled" size="md" leftSection={<HiMail />} mutation={email.sendEmailMute} disabled={email.selectedEmails.length == 0} className="">Send link via email</MutationButton></div><MutationError mutation={email.sendEmailMute} title="Error sharing via email" /></div>;
    }).with("invite", () => <div className="flex flex-col gap-6"><div><LabelForm className="mb-1">Link gives access to this workspace as:</LabelForm><Radio.Group value={accessType} onChange={t_3 => setAccessType(t_3 as AccessType)}><div className="flex gap-2 justify-stretch">{entries(accessTypeMd).map(t16 => {
              const [key, t_4] = t16;
              return <ShareRadioCard key={key} value={key} label={t_4.label} description={t_4.description} checked={key == accessType} />;
            })}</div></Radio.Group></div>{match(activeLink).with(P.not(undefined), link_1 => <InviteLinkCard link={link_1} form={inviteLinks.getInviteLinkForm(link_1.id)} copyButtonProps={{
        variant: "filled"
      }} hidePath={true} />).otherwise(() => <div className="flex flex-col gap-2"><Button variant="filled" size="sm" loading={inviteLinks.isPending} disabled={inviteLinks.isPending} onClick={() => {
          const accessMd = accessTypeMd[accessType].config;
          if (inviteLinks && accessMd) {
            inviteLinks.createInviteLink({
              name: `${fmtDate(D.now(), {
                disableRelative: true
              })} with access ${accessMd.roles.join("|")} to ${path ?? "Home"}`,
              anon: accessMd.anon,
              roles: [...accessMd.roles],
              expires: D.now().plus({
                days: 14
              }).toISO(),
              path
            });
          }
        }} className="self-end">{inviteLinks.isPending ? "Creating..." : "Create link"}</Button>{inviteLinks.isError && inviteLinks.error && <Alert color="red" title="Error with managing links">{inviteLinks.error.message}</Alert>}</div>)}</div>).exhaustive()}</div> : <div className="flex flex-col gap-4"><h3 className="text-lg font-semibold mb-2">Share this page with users that have access</h3><CopyLink url={url.url} hideUrl={true} buttonProps={{
      variant: "filled"
    }} className="self-end">Copy link</CopyLink></div>;
  let t17;
  if ($[27] !== t13.Provider || $[28] !== t14 || $[29] !== t15) {
    t17 = <t13.Provider value={t14}>{t15}</t13.Provider>;
    $[27] = t13.Provider;
    $[28] = t14;
    $[29] = t15;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t11.Dropdown || $[32] !== t12 || $[33] !== t17) {
    t18 = <t11.Dropdown classNames={t12}>{t17}</t11.Dropdown>;
    $[31] = t11.Dropdown;
    $[32] = t12;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== T0 || $[36] !== menuOpened || $[37] !== onMenuChange || $[38] !== t10 || $[39] !== t18 || $[40] !== t7) {
    t19 = <T0 opened={menuOpened} onChange={onMenuChange} closeOnClickOutside={t7}>{t10}{t18}</T0>;
    $[35] = T0;
    $[36] = menuOpened;
    $[37] = onMenuChange;
    $[38] = t10;
    $[39] = t18;
    $[40] = t7;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  return t19;
};
const ShareRadioCard = t0 => {
  const $ = _c(13);
  const {
    value,
    label,
    description,
    checked
  } = t0;
  const t1 = checked && "text-primary font-bold";
  let t2;
  if ($[0] !== t1) {
    t2 = twMerge("font-semibold flex items-center gap-2", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Radio.Indicator />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== label || $[4] !== t2) {
    t4 = <div className={t2}>{t3}{label}</div>;
    $[3] = label;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== description) {
    t5 = <div className="text-sm opacity-60">{description}</div>;
    $[6] = description;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== checked || $[9] !== t4 || $[10] !== t5 || $[11] !== value) {
    t6 = <Radio.Card value={value} checked={checked} radius="md" className=" !flex flex-col items-start gap-2 !p-2 hover:bg-gray-500/5 opacity-70 data-[checked]:opacity-100 data-[checked]:border-primary/20">{t4}{t5}</Radio.Card>;
    $[8] = checked;
    $[9] = t4;
    $[10] = t5;
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const EmailShareForm = t0 => {
  const $ = _c(65);
  const {
    emailCombo: store,
    selectedEmails,
    emailInput,
    setEmailInput,
    emailCompletionsQuery,
    addEmail,
    removeEmail,
    isValidEmail
  } = t0;
  const {
    workspace_id
  } = usePortal();
  const [userClosedDropdown, setUserClosedDropdown] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setUserClosedDropdown(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== emailInput) {
    t2 = [emailInput];
    $[1] = emailInput;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== emailCompletionsQuery.data || $[4] !== emailInput.length || $[5] !== store || $[6] !== userClosedDropdown) {
    t3 = () => {
      if (emailInput.length <= 2 && store.dropdownOpened) {
        store.closeDropdown();
        setUserClosedDropdown(false);
      } else {
        if (emailCompletionsQuery.data && emailCompletionsQuery.data.length > 0 && !store.dropdownOpened && emailInput.length > 2 && !userClosedDropdown) {
          store.openDropdown();
        } else {
          if ((!emailCompletionsQuery.data || emailCompletionsQuery.data.length == 0) && store.dropdownOpened) {
            store.closeDropdown();
          }
        }
      }
    };
    $[3] = emailCompletionsQuery.data;
    $[4] = emailInput.length;
    $[5] = store;
    $[6] = userClosedDropdown;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== emailCompletionsQuery.data || $[9] !== emailInput.length || $[10] !== store.dropdownOpened || $[11] !== userClosedDropdown) {
    t4 = [emailCompletionsQuery.data, store.dropdownOpened, emailInput.length, userClosedDropdown];
    $[8] = emailCompletionsQuery.data;
    $[9] = emailInput.length;
    $[10] = store.dropdownOpened;
    $[11] = userClosedDropdown;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== addEmail || $[14] !== emailInput || $[15] !== isValidEmail || $[16] !== workspace_id) {
    t5 = () => {
      if (emailInput.trim() && isValidEmail(emailInput)) {
        addEmail(emailInput.trim(), undefined, workspace_id);
      }
    };
    $[13] = addEmail;
    $[14] = emailInput;
    $[15] = isValidEmail;
    $[16] = workspace_id;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleAddEmail = t5;
  let t6;
  if ($[18] !== addEmail || $[19] !== emailCompletionsQuery.data || $[20] !== workspace_id) {
    t6 = email => {
      const existingProfile = emailCompletionsQuery.data?.find(u => u.email.toLowerCase() == email.toLowerCase());
      addEmail(email, existingProfile, workspace_id);
    };
    $[18] = addEmail;
    $[19] = emailCompletionsQuery.data;
    $[20] = workspace_id;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <LabelForm>Share via email to new or existing users</LabelForm>;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== setEmailInput) {
    t8 = e => setEmailInput(e.currentTarget.value);
    $[23] = setEmailInput;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== addEmail || $[26] !== workspace_id) {
    t9 = e_0 => {
      const txt = e_0.clipboardData.getData("text");
      const emails = txt.split(/\n/).map(_temp13).filter(_temp14);
      if (emails.length <= 1) {
        return;
      }
      e_0.preventDefault();
      emails.forEach(email_0 => addEmail(email_0, undefined, workspace_id));
    };
    $[25] = addEmail;
    $[26] = workspace_id;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== handleAddEmail) {
    t10 = e_2 => {
      if (e_2.key == "Enter") {
        e_2.preventDefault();
        handleAddEmail();
      }
    };
    $[28] = handleAddEmail;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== store) {
    t11 = () => {
      setTimeout(() => {
        store.closeDropdown();
        setUserClosedDropdown(true);
      }, 150);
    };
    $[30] = store;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== emailInput || $[33] !== isValidEmail) {
    t12 = !emailInput.trim() || !isValidEmail(emailInput);
    $[32] = emailInput;
    $[33] = isValidEmail;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <HiPlusCircle />;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== handleAddEmail || $[37] !== t12) {
    t14 = <ActionIcon variant="subtle" size="md" onClick={handleAddEmail} disabled={t12} className="hover:bg-gray-100 mr-1">{t13}</ActionIcon>;
    $[36] = handleAddEmail;
    $[37] = t12;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== emailInput || $[40] !== t10 || $[41] !== t11 || $[42] !== t14 || $[43] !== t8 || $[44] !== t9) {
    t15 = <Combobox.Target><TextInput placeholder="Type or paste email addresses..." value={emailInput} onChange={t8} onPaste={t9} onKeyDown={t10} onBlur={t11} className="w-full" size="md" rightSection={t14} /></Combobox.Target>;
    $[39] = emailInput;
    $[40] = t10;
    $[41] = t11;
    $[42] = t14;
    $[43] = t8;
    $[44] = t9;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Combobox.Header className="opacity-70">Matching emails in workspaces you administer</Combobox.Header>;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== emailCompletionsQuery || $[48] !== workspace_id) {
    let t18;
    if ($[50] !== workspace_id) {
      t18 = t19 => {
        const {
          data
        } = t19;
        return data.length == 0 ? <div className="p-4 text-center text-gray-500">None found</div> : data.map(u_0 => <Combobox.Option key={u_0.email} value={u_0.email} className={"space-y-2 data-[combobox-selected=\"true\"]:!bg-primary/10 !text-black/90"}><div className="flex items-center gap-2"><span className="font-semibold">{u_0.email}</span>{u_0.name && <span className="opacity-60">({u_0.name})</span>}</div><div className="flex gap-2 flex-wrap">{(() => {
              const sortedWorkspaces = unique(u_0.workspaces).sort((a, b) => {
                const aIndex = u_0.workspace_ids[u_0.workspaces.indexOf(a)];
                const bIndex = u_0.workspace_ids[u_0.workspaces.indexOf(b)];
                return aIndex === workspace_id ? -1 : bIndex === workspace_id ? 1 : 0;
              });
              const visibleWorkspaces = sortedWorkspaces.slice(0, 6);
              const remainingCount = sortedWorkspaces.length - 6;
              return <>{visibleWorkspaces.map(w => {
                  const workspaceId = u_0.workspace_ids[u_0.workspaces.indexOf(w)];
                  const isCurrentWorkspace = workspaceId === workspace_id;
                  return <Badge key={w} color={isCurrentWorkspace ? "blue" : "gray"} variant={isCurrentWorkspace ? "filled" : "light"} size="xs">{isCurrentWorkspace ? "this workspace" : w}</Badge>;
                })}{remainingCount > 0 && <span className="text-xs text-gray-500">and {remainingCount} more workspace{remainingCount > 1 ? "s" : ""}</span>}</>;
            })()}</div></Combobox.Option>);
      };
      $[50] = workspace_id;
      $[51] = t18;
    } else {
      t18 = $[51];
    }
    t17 = match(emailCompletionsQuery).with({
      isLoading: true
    }, _temp15).with({
      data: P.not(P.nullish)
    }, t18).otherwise(_temp16);
    $[47] = emailCompletionsQuery;
    $[48] = workspace_id;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[52] !== t17) {
    t18 = <Combobox.Dropdown className="max-h-[50vh] overflow-y-auto !max-w-[40em]">{t16}<Combobox.Options className="space-y-2">{t17}</Combobox.Options></Combobox.Dropdown>;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== store || $[55] !== t15 || $[56] !== t18 || $[57] !== t6) {
    t19 = <div className="flex flex-col gap-2"><Combobox store={store} onOptionSubmit={t6}>{t7}{t15}{t18}</Combobox></div>;
    $[54] = store;
    $[55] = t15;
    $[56] = t18;
    $[57] = t6;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== removeEmail || $[60] !== selectedEmails) {
    t20 = selectedEmails.length > 0 && <div className="flex flex-wrap gap-2">{selectedEmails.map(t21 => {
        const {
          email: email_1,
          isExisting,
          profile,
          isInCurrentWorkspace
        } = t21;
        return <div key={email_1} className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">{profile ? <SAvatar size="xs" profile={profile} /> : null}<span className={isExisting ? "text-blue-600" : "text-gray-700"}>{email_1}</span>{isInCurrentWorkspace && <Badge color="blue" variant="filled" size="xs">this workspace</Badge>}<CloseButton size="xs" onClick={() => removeEmail(email_1)} className="ml-1" /></div>;
      })}</div>;
    $[59] = removeEmail;
    $[60] = selectedEmails;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== t19 || $[63] !== t20) {
    t21 = <div className=" flex flex-col gap-4">{t19}{t20}</div>;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  return t21;
};
function _temp(e) {
  return !e.isInCurrentWorkspace;
}
function _temp2(r) {
  return r != "report_viewer";
}
function _temp3(email_1) {
  const emailSchema = z.string().email();
  const result_0 = emailSchema.safeParse(email_1.trim());
  return result_0.success;
}
function _temp4(e_2) {
  return e_2.email;
}
function _temp5(link_0, config) {
  return link_0.anon == config.anon && link_0.roles.length == config.roles.length && link_0.roles.every(r => (config.roles as readonly string[]).includes(r));
}
function _temp6(a, b) {
  if (!a.expires && !b.expires) {
    return 0;
  }
  if (!a.expires) {
    return -1;
  }
  if (!b.expires) {
    return 1;
  }
  return D.fromISO(b.expires).diff(D.fromISO(a.expires)).milliseconds;
}
function _temp7(a_0, b_0) {
  return D.fromISO(b_0.created).diff(D.fromISO(a_0.created)).milliseconds;
}
function _temp8(t_0) {
  return t_0 ? t_0[0] : undefined;
}
function _temp9() {
  return "Known users";
}
function _temp10() {
  return "Anyone with a link";
}
function _temp11(p) {
  return p.roles.length > 0;
}
function _temp12(r_0) {
  return {
    value: r_0,
    label: r_0.charAt(0).toUpperCase() + r_0.slice(1)
  };
}
function _temp13(t) {
  return t.trim();
}
function _temp14(e_1) {
  return e_1 != "";
}
function _temp15() {
  return <div className="p-4 text-center text-gray-500">Loading...</div>;
}
function _temp16() {
  return <div className="p-4 text-center text-gray-500">No results</div>;
}